import { ChangeEvent, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import type { Dispatch } from 'redux';

import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { DecisionDocuments } from 'DecisionPackage/DecisionDocumentsModule/components/DecisionDocuments';
import { DecisionViolations } from 'DecisionPackage/DecisionFoundViolationsModule/components/DecisionViolations';
import { DecisionInfo } from 'DecisionPackage/DecisionInfoModule/components/DecisionInfo';
import { DecisionJudgment } from 'DecisionPackage/DecisionJudgmentModule/components/DecisionJudgment';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { KnmEpguNotification } from 'KnmPackage/KnmEpguNotificationModule/components/KnmEpguNotification';
import { KnmSignInfo } from 'KnmPackage/KnmSignInfoModule/components/KnmSignInfo';
import { DecisionAct } from 'DecisionPackage/DecisionAct/DecisionAct';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { DecisionWillBeAct } from 'DecisionPackage/DecisionWillBeAct/components/DecisionWillBeAct';
import { getKnmDataVersion, getKnmIsNeedActSaveOrSign } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { ErknmAgreements } from 'KnmErknmPackage/ErkmAgreementsModule/ErknmAgreements';
import { ErknmRepeatPassports } from 'KnmErknmPackage/ErknmRepeatPassportsModule/ErknmRepeatPassports';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';

import { DecisionSubject } from '../../DecisionSubjectModule/components/DecisionSubject';
import { decisionResetInitialFormValues } from '../actions/decisionActions';
import { useDecision } from '../hooks/useDecision';
import { getIsDecisionViolationsInBody } from '../common/getIsDecisionViolationsInBody';
import { IDecisionForm } from '../interfaces/IDecisionForm';
import { IDecisionFormRef } from '../interfaces/IDecisionFormRef';
import {
  getDecisionEventTypesSelectOptions,
  getDecisionFormRef,
  getDecisionInitialFormValues,
  getDecisionIsCommonWatchOnly,
  getDecisionStatus,
  getDecisionWatchOnlyByTestUser,
  getKnmIsDecisionStatusTypeCompleted,
  getKnmIsDecisionStatusTypeDisapproved,
} from '../selectors/decisionSelectors';
import { DecisionAppealStatus } from './DecisionAppealStatus';
import { DecisionResults } from '../../DecisionResultModule/components/DecisionsResultList';
import { DecisionEvents } from '../../DecisionEventsModule/components/DecisionEvents';
import { IDictionaryEventTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { DecisionHistories } from './DecisionHistories';
import { knmErvkProsecutorGet } from '../../../KnmPackage/MainModule/actions/knmActions';
import { IUserOrganisationSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getAccountProsecutorOrganizationsWithDistrictSelectOptions } from '../../../CorePackage/AccountModule/selectors/accountSelectors';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import styles from '../../../KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActEvents/ErknmActEventItemBody.module.scss';
import { ErknmEventUav } from '../../../KnmErknmPackage/ErknmEventsModule/components/ErknmEventUav';
import { Row } from '../../../UIPackage/FormModule/components/Row';

interface IDecisionContentProps {
  decisionData: IDecision | undefined;
  values: IDecisionForm;
}

interface IDecisionContentStoreProps {
  eventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  formRef: IDecisionFormRef | undefined;
  initialFormValues?: IDecisionForm;
  isDecisionStatusTypeCompleted: boolean;
  isDecisionStatusTypeDisapproved: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  isWatchOnly: boolean;
  statusCode?: IKnmStatus;
  settings: IAdminSetting[];
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  isWatchOnlyByTestUser: boolean;
  isNeedActSaveOrSign?: boolean;
  isErp: boolean;
}

const DecisionContentWithoutStore = ({
  decisionData,
  values,
  eventTypesSelectOptions,
  formRef,
  initialFormValues,
  isDecisionStatusTypeCompleted,
  isDecisionStatusTypeDisapproved,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  isWatchOnly,
  statusCode,
  settings,
  userProsecutorOrganisationsSelectOptions,
  isWatchOnlyByTestUser,
  isNeedActSaveOrSign = false,
  isErp,
}: IDecisionContentProps & IDecisionContentStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { created, organization, isOrganizationNew, objects } = values;
  const isDisableJudgment = !isSupervisor || (!isDecisionStatusTypeCompleted && !isDecisionStatusTypeDisapproved);
  const isDisableDocuments = !isSupervisor && !isProsecutorOrSuperProsecutor;

  const isUse4151 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_16_STARTS_FROM,
  );

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const isEnableAgreements = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_16_STARTS_FROM,
  );

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const isLater4111ForDecision = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.DECISION_SUBJECT_TYPE_4_1_1_START_FROM,
  );

  const isLaterThanKnmValidationSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_53_STARTS_FROM,
  );

  const decisionMinEventStartDateTime = isLaterThanKnmValidationSetting
    ? values.reasons?.[0]?.prescriptionDate || undefined || undefined
    : '';

  useEffect(() => {
    formRef?.current.reset(initialFormValues);
  }, [formRef, initialFormValues]);

  useDecision(values);

  useUnmount(() => {
    dispatch(decisionResetInitialFormValues());
  });

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.INTERACTION_CATEGORIES));
    dispatch(catalogGet(CommonCatalogsNamesEnum.UAV_TYPES));
  }, [dispatch]);

  useEffect(() => {
    if (decisionData?.prosecutorOrganization?.ervkUuid && decisionData?.prosecutorOrganization?.ervkVersionUuid) {
      dispatch(
        knmErvkProsecutorGet(
          decisionData.prosecutorOrganization.ervkUuid,
          decisionData.prosecutorOrganization.ervkVersionUuid,
        ),
      );
    }
  }, [decisionData]);

  useEffect(() => {
    if (userProsecutorOrganisationsSelectOptions && !decisionData?.id) {
      const byDefault = userProsecutorOrganisationsSelectOptions.find(i => i.byDefault);

      if (byDefault) {
        formRef?.current.change('prosecutorOrganization', byDefault);
      }
    }
  }, [formRef, values.prosecutorOrganization, userProsecutorOrganisationsSelectOptions]);

  const isVO = values.kind ? values.kind.label === 'Выездное обследование' : undefined;
  const isOT = values.kind ? values.kind.label === 'Наблюдение за соблюдением обязательных требований' : undefined;
  const isShowUav = (isVO || isOT) && !isErp;

  const isViolationsInBody = getIsDecisionViolationsInBody({
    created,
    settings,
    isNeedToAddAct: values.isNeedToAddAct,
    act: values.organization?.act,
  });

  const onChangeIsUavUsed = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      // @ts-ignore
      formRef?.current.change(`uavTechnologies`);
    }
  };

  return (
    <>
      <DecisionInfo
        created={created}
        kindControlFormValue={values.kindControl}
        reasons={values.reasons}
        watchOnly={isWatchOnly}
        statusCode={statusCode}
      />
      <DecisionSubject
        created={created}
        organization={organization}
        isOrganizationNew={isOrganizationNew}
        reasons={values.reasons}
      />
      {isShowUav && (
        <Row>
          <FieldBlock>
            <CheckboxField
              className={styles.FilterFormGroupCheckbox}
              id={`isUavUsed`}
              name={`isUavUsed`}
              label="Проводились с использованием беспилотных технологий"
              theme="system"
              type="checkbox"
              size="medium"
              onChangeCallback={onChangeIsUavUsed}
            />
          </FieldBlock>
          {values.isUavUsed && (
            <ErknmEventUav id={`uavTechnologies`} name={`uavTechnologies`} watchOnly={isWatchOnly} isDecision={true} />
          )}
        </Row>
      )}
      {eventTypesSelectOptions?.length && !(isDecisionStatusTypeCompleted && !values.events?.length) ? (
        <DecisionEvents
          isWatchOnly={
            isWatchOnly || isDecisionStatusTypeCompleted || isProsecutorOrSuperProsecutor || isNeedActSaveOrSign
          }
          eventTypesSelectOptions={eventTypesSelectOptions}
          decisionMinEventStartDateTime={decisionMinEventStartDateTime}
        />
      ) : (
        <></>
      )}
      {isUse4151 && <DecisionWillBeAct watchOnly={isWatchOnly} act={values.organization?.act} />}
      {isViolationsInBody && (
        <DecisionViolations
          watchOnly={isWatchOnly}
          isViolationFieldsWatchOnly={isWatchOnly}
          isRequirements={values.isViolatedMandatoryRequirements}
          knoOrganization={values.knoOrganization}
          supervisionType={values.kindControl}
          objects={objects}
          reasons={values.reasons}
          created={created}
          inBody
        />
      )}
      {isUse4151 && (
        <DecisionAct
          reasons={values.reasons}
          title="Сведения об акте"
          formRef={formRef}
          isWatchOnly={isWatchOnly}
          values={values}
          isEnablePaperlessDocument={isEnablePaperlessDocument}
        />
      )}
      <DecisionDocuments
        watchOnly={isWatchOnly || isDisableDocuments || isNeedActSaveOrSign}
        formInstance={formRef?.current}
        title="Дополнительные документы, относящиеся к решению"
        disableDeleteDocuments={isWatchOnly}
      />
      <DecisionResults
        isWatchOnly={isWatchOnly || isNeedActSaveOrSign}
        violatedRequirementsNpa={values.violatedMandatoryRequirements}
        title="Сведения о решениях"
        organization={values.organization}
        agreements={values.agreements}
        isLater4111ForDecision={isLater4111ForDecision}
        isEnablePaperlessDocument={isEnablePaperlessDocument}
      />
      {isEnableAgreements && (
        <ErknmAgreements decisionOrganization={values.organization} agreements={values.agreements} isDecision />
      )}
      <KnmSignInfo
        title="Сведения о подписании"
        notificationText="Решение ранее не было подписано"
        signaturesInfo={decisionData?.signatureInfo}
        knmDocuments={decisionData?.documents}
        isDecision
      />
      <KnmEpguNotification
        organizations={organization ? (([organization] as unknown) as IKnmErknmFormSubject[]) : undefined}
        isWatchOnlyByTestUser={isWatchOnlyByTestUser}
        type={'DECISION_PASSPORT'}
      />
      <DecisionAppealStatus />
      <DecisionJudgment
        watchOnly={isWatchOnly || isDisableJudgment || isNeedActSaveOrSign}
        formRef={formRef}
        startDate={decisionData?.basisDocument?.dateTime}
      />
      {isEnablePrescriptionCheck && <ErknmRepeatPassports repeatPassports={values?.repeatPassports} />}
      <DecisionHistories title="История изменений решения" executionTitle="История проведения решения" />
    </>
  );
};

const mapStateToProps = (state: IRootState): IDecisionContentStoreProps => ({
  eventTypesSelectOptions: getDecisionEventTypesSelectOptions(state),
  formRef: getDecisionFormRef(state),
  initialFormValues: getDecisionInitialFormValues(state),
  isDecisionStatusTypeCompleted: getKnmIsDecisionStatusTypeCompleted(state),
  isDecisionStatusTypeDisapproved: getKnmIsDecisionStatusTypeDisapproved(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isWatchOnly: getDecisionIsCommonWatchOnly(state),
  statusCode: getDecisionStatus(state),
  settings: getAdminFetchSettings(state),
  userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsWithDistrictSelectOptions(state),
  isWatchOnlyByTestUser: getDecisionWatchOnlyByTestUser(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  isErp: getKnmDataVersion(state) === KnmVersionsEnum.ERP,
});

export const DecisionContent = connect(mapStateToProps)(DecisionContentWithoutStore);
